import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Group, GroupDetailLight } from '../../user/model/user';

/**
 * Provides data access to the Group API.
 */
@Injectable({ providedIn: 'root' })
export class GroupService
{
  constructor(private http: HttpClient) { }

  /**
   * Get all groups.
   *
   * @param admin When true, appends admin=true (org-scoped list for some roles).
   * @param dataLoad Optional: {@code listOnly} for lightweight rows with {@code userCount} only; omit or {@code full} for legacy full payload.
   */
  public getGroups(admin: boolean = null, dataLoad: 'full' | 'listOnly' = null): Observable<Group[]>
  {
    const params: string[] = [];
    if (admin)
    {
      params.push('admin=true');
    }
    if (dataLoad)
    {
      params.push(`dataLoad=${encodeURIComponent(dataLoad)}`);
    }
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    return this.http.get<Group[]>(`/group/groups${query}`);
  }

  /**
   * Persist a Group in the database.
   *
   * @param data Data to save
   * @param isNew Is this a new Group?
   */
  public persistGroup(
    group: Group,
    isNew: boolean,
    groupId?: number,
    dataLoad: 'full' | 'light' = 'full'): Observable<Group | GroupDetailLight>
  {
    if (isNew)
    {
      return this.http.post<Group>('/group', group);
    }
    const query = dataLoad === 'light' ? '?dataLoad=light' : '';
    return this.http.put<Group | GroupDetailLight>(`/group/${groupId}${query}`, group);
  }

  /**
   * Get a Group by ID ({@code dataLoad=full}, default).
   */
  public getGroup(id: number): Observable<Group>
  {
    return this.http.get<Group>('/group/' + id);
  }

  /**
   * Group header only (no members), for edit/delete UIs that load members separately.
   */
  public getGroupDetailLight(id: number): Observable<GroupDetailLight>
  {
    return this.http.get<GroupDetailLight>(`/group/${id}?dataLoad=light`);
  }

  /**
   * Delete a group.
   *
   * @param groupId Group id
   */
  public deleteGroup(groupId: number): Observable<Group>
  {
    return this.http.delete<Group>(`/group/${groupId}`);
  }

  /**
   * Check if Group is in use
   *
   * @param data Group Id
   */
  public existsGroup(id: number): Observable<any>
  {
    return this.http.get(`/group/inuse/${id}`);
  }

  public addUserToGroup(userId: number, groupIds: Array<number>): Observable<any>
  {
    return this.http.put(`/group/user/${userId}`, groupIds);
  }

}
